import { isValidCNPJ, isValidCPF } from "./documents";
import { DomainError } from "./errors";

// ===== Cobrança Pix (T1.10): BR Code (EMV MPM do BACEN), chave, txid e alocação do pagamento =====

/** GUI do arranjo Pix no campo 26 (Merchant Account Information). */
export const PIX_GUI = "br.gov.bcb.pix";
/** Campo 54 tem até 13 caracteres ("9999999999.99"). */
export const MAX_PIX_CENTS = 999_999_999_999;
/** Validade da cobrança imediata (calendario.expiracao, em segundos). */
export const PIX_MIN_EXPIRATION_S = 300;
export const PIX_MAX_EXPIRATION_S = 30 * 24 * 3600;
export const PIX_DEFAULT_EXPIRATION_S = 24 * 3600;
/** Cobranças (de qualquer situação) por título. */
export const MAX_PIX_CHARGES_PER_TITLE = 20;
/** Pagamentos por notificação de webhook. */
export const MAX_PIX_WEBHOOK_PAYMENTS = 100;
/** Nome do recebedor (59) e cidade (60) no BR Code. */
export const PIX_MERCHANT_NAME_MAX = 25;
export const PIX_MERCHANT_CITY_MAX = 15;
/** txid da cob (API Pix do BACEN): 26 a 35 alfanuméricos. No BR Code estático, até 25. */
export const PIX_TXID_RE = /^[a-zA-Z0-9]{26,35}$/;
const STATIC_TXID_RE = /^[a-zA-Z0-9]{1,25}$/;
/** endToEndId: E + ISPB (8) + data/hora + sequencial, 32 alfanuméricos. */
export const PIX_E2E_RE = /^[a-zA-Z0-9]{32}$/;

const fail = (code: string, message: string): never => {
  throw new DomainError(code, message);
};

/** CRC-16/CCITT-FALSE (polinômio 0x1021, inicial 0xFFFF) sobre os bytes UTF-8, em 4 dígitos hexadecimais maiúsculos. */
export function crc16(payload: string): string {
  let crc = 0xffff;
  for (const byte of new TextEncoder().encode(payload)) {
    crc ^= byte << 8;
    for (let i = 0; i < 8; i++) crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
  }
  return crc.toString(16).toUpperCase().padStart(4, "0");
}

const PRINTABLE = /^[\x20-\x7E]*$/;

/** Campo TLV do EMV: id de 2 dígitos, tamanho de 2 dígitos (até 99) e valor ASCII imprimível. */
export function emvField(id: string, value: string): string {
  if (!/^\d{2}$/.test(id)) fail("invalid_pix_payload", `Campo EMV inválido: ${id}`);
  if (!PRINTABLE.test(value)) fail("invalid_pix_payload", `Campo ${id} com caractere não permitido.`);
  if (value.length === 0 || value.length > 99)
    fail("invalid_pix_payload", `Campo ${id} com tamanho inválido.`);
  return `${id}${String(value.length).padStart(2, "0")}${value}`;
}

/**
 * Texto para nome e cidade do recebedor: sem acentos, só letras, dígitos e espaço (o que todos os apps aceitam),
 * espaços colapsados e cortado no máximo do campo.
 */
export function pixText(s: string, max: number): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Za-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max)
    .trim();
}

/** Centavos → "1234.56" (campo 54 e valor.original da cob). */
export function formatPixAmount(cents: number): string {
  if (!Number.isSafeInteger(cents) || cents < 1 || cents > MAX_PIX_CENTS)
    fail("invalid_money", "Valor do Pix inválido.");
  return `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, "0")}`;
}

/** "1234.56" → centavos. Formato da API Pix: até 10 dígitos inteiros e exatamente 2 decimais. */
export function parsePixAmount(s: string): number {
  const m = /^(\d{1,10})\.(\d{2})$/.exec(s);
  if (!m) return fail("invalid_money", "Valor do Pix inválido.");
  const cents = Number(m[1]) * 100 + Number(m[2]);
  if (cents < 1) fail("invalid_money", "Valor do Pix inválido.");
  return cents;
}

export const PIX_KEY_KINDS = ["cpf", "cnpj", "email", "phone", "evp"] as const;
export type PixKeyKind = (typeof PIX_KEY_KINDS)[number];

const EVP_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const EMAIL_RE = /^[a-z0-9._%+-]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/;
const PHONE_RE = /^\+55[1-9]{2}9?\d{8}$/;

/**
 * Normaliza a chave Pix como o DICT a guarda: CPF/CNPJ só dígitos, e-mail minúsculo, celular "+55DDDNÚMERO", aleatória
 * (EVP) minúscula. Chave inválida = `invalid_pix_key`. CPF/CNPJ só são aceitos com dígito verificador válido.
 */
export function normalizePixKey(input: string): { key: string; kind: PixKeyKind } {
  const raw = input.trim();
  const lower = raw.toLowerCase();
  if (EVP_RE.test(lower)) return { key: lower, kind: "evp" };
  if (raw.includes("@")) {
    if (lower.length <= 77 && EMAIL_RE.test(lower)) return { key: lower, kind: "email" };
    return fail("invalid_pix_key", "Chave Pix inválida: e-mail mal formado.");
  }
  if (raw.startsWith("+")) {
    const phone = `+${raw.replace(/\D/g, "")}`;
    if (PHONE_RE.test(phone)) return { key: phone, kind: "phone" };
    return fail("invalid_pix_key", "Chave Pix inválida: celular no formato +55 DDD número.");
  }
  if (/^[\d.\-/ ]+$/.test(raw)) {
    const digits = raw.replace(/\D/g, "");
    if (digits.length === 11 && isValidCPF(digits)) return { key: digits, kind: "cpf" };
    if (digits.length === 14 && isValidCNPJ(digits)) return { key: digits, kind: "cnpj" };
  }
  return fail(
    "invalid_pix_key",
    "Chave Pix inválida: use CPF, CNPJ, e-mail, celular (+55) ou chave aleatória.",
  );
}

export type PixPayloadInput = {
  /** Estático: a chave. Dinâmico: omita e informe `location`. */
  key?: string;
  /** Dinâmico: URL do payload (sem "https://"), campo 26.25. */
  location?: string;
  merchantName: string;
  merchantCity: string;
  amountCents?: number;
  /** Só no estático (até 25); no dinâmico o txid fica no payload da location e o campo 62.05 é "***". */
  txid?: string;
  /** Campo 01 = "12" (uso único). */
  uniqueUse?: boolean;
};

/** Monta o BR Code ("Pix copia e cola") com o CRC16 do campo 63. */
export function buildPixPayload(i: PixPayloadInput): string {
  if ((i.key === undefined) === (i.location === undefined))
    fail("invalid_pix_payload", "Informe a chave (estático) ou a location (dinâmico).");
  const name = pixText(i.merchantName, PIX_MERCHANT_NAME_MAX);
  const city = pixText(i.merchantCity, PIX_MERCHANT_CITY_MAX);
  if (!name) fail("invalid_pix_payload", "Informe o nome do recebedor.");
  if (!city) fail("invalid_pix_payload", "Informe a cidade do recebedor.");
  let txid = "***";
  if (i.txid !== undefined) {
    if (i.location !== undefined) fail("invalid_pix_payload", "No Pix dinâmico o txid vai na location.");
    if (!STATIC_TXID_RE.test(i.txid)) fail("invalid_txid", "txid do Pix estático: até 25 letras e dígitos.");
    txid = i.txid;
  }
  const account =
    emvField("00", PIX_GUI) +
    (i.key !== undefined ? emvField("01", i.key) : emvField("25", i.location!.replace(/^https?:\/\//, "")));
  const body =
    emvField("00", "01") +
    (i.uniqueUse ? emvField("01", "12") : "") +
    emvField("26", account) +
    emvField("52", "0000") +
    emvField("53", "986") +
    (i.amountCents !== undefined ? emvField("54", formatPixAmount(i.amountCents)) : "") +
    emvField("58", "BR") +
    emvField("59", name) +
    emvField("60", city) +
    emvField("62", emvField("05", txid)) +
    "6304";
  return body + crc16(body);
}

/** Lê os campos TLV de um nível (sem descer nos templates). Tamanho que passa do fim = inválido. */
export function parseEmvFields(s: string): Map<string, string> {
  const out = new Map<string, string>();
  let i = 0;
  while (i < s.length) {
    const id = s.slice(i, i + 2);
    const len = Number(s.slice(i + 2, i + 4));
    if (!/^\d{2}$/.test(id) || !/^\d{2}$/.test(s.slice(i + 2, i + 4)) || i + 4 + len > s.length)
      fail("invalid_pix_payload", "BR Code mal formado.");
    out.set(id, s.slice(i + 4, i + 4 + len));
    i += 4 + len;
  }
  return out;
}

export type ParsedPix = {
  key: string | null;
  location: string | null;
  amountCents: number | null;
  merchantName: string;
  merchantCity: string;
  txid: string;
};

/**
 * Confere e lê um BR Code (o que o provedor devolve é validado antes de ir para a tela): CRC, formato "01", GUI do Pix,
 * moeda 986, país BR, nome e cidade. Qualquer desvio = `invalid_pix_payload`.
 */
export function parsePixPayload(s: string): ParsedPix {
  if (!PRINTABLE.test(s) || s.length < 8 || s.slice(-8, -4) !== "6304")
    fail("invalid_pix_payload", "BR Code mal formado.");
  if (crc16(s.slice(0, -4)) !== s.slice(-4).toUpperCase())
    fail("invalid_pix_payload", "CRC do BR Code inválido.");
  const f = parseEmvFields(s.slice(0, -8));
  const account = parseEmvFields(f.get("26") ?? "");
  if (f.get("00") !== "01" || account.get("00")?.toLowerCase() !== PIX_GUI)
    fail("invalid_pix_payload", "BR Code não é Pix.");
  if (f.get("53") !== "986" || f.get("58") !== "BR" || !f.get("59") || !f.get("60"))
    fail("invalid_pix_payload", "BR Code incompleto.");
  const amount = f.get("54");
  const m = amount === undefined ? null : /^(\d{1,10})(?:\.(\d{1,2}))?$/.exec(amount);
  if (amount !== undefined && !m) fail("invalid_pix_payload", "Valor do BR Code inválido.");
  const cents = m ? Number(m[1]) * 100 + Number((m[2] ?? "").padEnd(2, "0")) : null;
  if (cents === 0) fail("invalid_pix_payload", "Valor do BR Code inválido.");
  return {
    key: account.get("01") ?? null,
    location: account.get("25") ?? null,
    amountCents: cents,
    merchantName: f.get("59")!,
    merchantCity: f.get("60")!,
    txid: parseEmvFields(f.get("62") ?? "").get("05") ?? "***",
  };
}

const TXID_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

/** txid aleatório de 32 caracteres (62 símbolos, rejeição de viés), válido para a cob do BACEN. */
export function newTxid(
  randomBytes: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n)),
) {
  let out = "";
  while (out.length < 32)
    for (const b of randomBytes(48)) {
      if (b < 248 && out.length < 32) out += TXID_ALPHABET[b % 62];
    }
  return out;
}

export const isValidTxid = (s: string): boolean => PIX_TXID_RE.test(s);

export const PIX_REVIEW_REASONS = ["underpaid", "overpaid", "title_closed"] as const;
export type PixReviewReason = (typeof PIX_REVIEW_REASONS)[number];

/**
 * Como um Pix recebido vira baixa (padrão do produto, T1.10): multa, depois juros (até os da cobrança), depois
 * principal (até o saldo em aberto do título). O que sobra é excedente (não vira crédito automático).
 * - título fechado (quitado/cancelado) ou sem saldo: nenhuma baixa, tudo excedente, revisão `title_closed`;
 * - principal zero (pagou só encargos): nenhuma baixa (a baixa exige valor), tudo excedente, revisão `underpaid`;
 * - excedente > 0 (passou do saldo): baixa até o saldo, revisão `overpaid`;
 * - senão, recebido < valor da cobrança: baixa do que chegou, revisão `underpaid`.
 */
export function allocatePixPayment(i: {
  receivedCents: number;
  chargeAmountCents: number;
  interestCents: number;
  fineCents: number;
  openCents: number;
  titleOpen: boolean;
}): {
  settleCents: number;
  interestCents: number;
  fineCents: number;
  excessCents: number;
  review: PixReviewReason | null;
} {
  const none = (review: PixReviewReason) => ({
    settleCents: 0,
    interestCents: 0,
    fineCents: 0,
    excessCents: i.receivedCents,
    review,
  });
  if (!i.titleOpen || i.openCents <= 0) return none("title_closed");
  const fineCents = Math.min(i.receivedCents, i.fineCents);
  let rest = i.receivedCents - fineCents;
  const interestCents = Math.min(rest, i.interestCents);
  rest -= interestCents;
  const settleCents = Math.min(rest, i.openCents);
  if (settleCents === 0) return none("underpaid");
  const excessCents = rest - settleCents;
  const review: PixReviewReason | null =
    excessCents > 0 ? "overpaid" : i.receivedCents < i.chargeAmountCents ? "underpaid" : null;
  return { settleCents, interestCents, fineCents, excessCents, review };
}
